import React from 'react';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from 'common/CustomDatePicker/CustomDatePicker';
import { ScheduleEmployeeType } from 'store/scheduleEmployees/interfaces';
import { PlannerEmployee } from 'pages/WorkSchedulesPlanner/interfaces';
import EmployeeSummary from 'pages/WorkSchedulesPlanner/EmployeeWorkSchedule/EmployeeSummary/EmployeeSummary';
import { startOfDay } from 'date-fns';

interface DateFormProps {
  employee: PlannerEmployee;
}

export default function DateForm({ employee }: DateFormProps) {
  const { t } = useTranslation();

  const { control, getValues, setValue, trigger } = useFormContext();

  return (
    <Stack
      width="30%"
      borderRight="0.1rem solid"
      borderColor="border.main"
      py={3}
      pr={3}
      spacing={3}
    >
      <Stack spacing={2}>
        <Typography variant="inputLabel" color="text.secondary">
          {t('employees.header_name')}
        </Typography>
        <EmployeeSummary employee={employee} />
      </Stack>
      <Divider />
      <Controller
        name="dateFrom"
        control={control}
        render={({ field: { value, onChange, onBlur } }) => (
          <CustomDatePicker
            label={t('holidays.from')}
            views={['year', 'month', 'day']}
            sx={{ width: '100%' }}
            value={value}
            onChange={onChange}
            onClose={onBlur}
            minDate={startOfDay(new Date())}
          />
        )}
      />
      <FormControlLabel
        control={
          <Checkbox
            value={getValues('type')}
            checked={getValues('type') === ScheduleEmployeeType.exception}
            onChange={(event) => {
              setValue(
                'type',
                event.target.checked
                  ? ScheduleEmployeeType.exception
                  : ScheduleEmployeeType.normal,
              );
              trigger();
            }}
          />
        }
        label={<Typography variant="body1">{t('label.exception')}</Typography>}
      />
      <Controller
        name="dateTo"
        control={control}
        render={({ field: { value, onChange, onBlur } }) => (
          <CustomDatePicker
            label={t('holidays.to')}
            views={['year', 'month', 'day']}
            sx={{
              width: '100%',
              visibility:
                getValues('type') === ScheduleEmployeeType.exception
                  ? 'visible'
                  : 'hidden',
            }}
            value={value}
            onChange={(onChangeValue, context) => {
              onChange(onChangeValue, context);
              trigger();
            }}
            onClose={onBlur}
            minDate={getValues('dateFrom')}
          />
        )}
      />
    </Stack>
  );
}
